import Button from '@jetbrains/ring-ui/components/button/button'
import Dialog from '@jetbrains/ring-ui/components/dialog/dialog'
import {H3} from '@jetbrains/ring-ui/components/heading/heading'
import {Content, Header} from '@jetbrains/ring-ui/components/island/island'
import Panel from '@jetbrains/ring-ui/components/panel/panel'
import classNames from 'classnames'
import * as React from 'react'
import {useCallback, useContext} from 'react'

import usePrevious from '../../../../../../hooks/usePrevious'
import {mergeIfDifferent} from '../../../../../../reducers/utils'
import {restApi} from '../../../../../../services/rest'
import {noop} from '../../../../../../utils/empty'
import {resolveHelpURL} from '../../../../../../utils/url'
import HelpLink from '../../../../../common/HelpLink/HelpLink'
import IconButton from '../../../../../common/IconButton/IconButton'
import {GetRulesArgContext} from '../../../CleanupProjectPage.contexts'
import {useIsReadOnly} from '../../../CleanupProjectPage.hooks'
import Form from '../../../Form/Form'
import type {Rule} from '../Rules.types'
import {getKey, getRuleOwnership} from '../Rules.utils'

import {LoadingContext, DispatchContext} from './CleanupForm.contexts'
import type {
  CleanupFormAction,
  CleanupFormState,
  Props,
  CleanupFormValidationErrors,
} from './CleanupForm.types'
import {adaptRuleUiToApi} from './CleanupForm.utils'
import BranchesSection, {
  branchesActivityConverter,
  branchPatternConverter,
  branchPatternReducer,
} from './Sections/BranchesSection'
import BuildStatusSection, {buildStatusConverter} from './Sections/BuildStatusSection'
import LimitsSection, {defaultLimitValueData} from './Sections/LimitsSection'
import PersonalSection, {personalConverter} from './Sections/PersonalSection'
import PreserveDependencies, {defaultPreserveArtifacts} from './Sections/PreserveDependencies'
import PreserveSection, {preserveConverter} from './Sections/PreserveSection'
import TagSection, {tagsConverter} from './Sections/TagsSection'
import WillApplySection, {willApplyConverter} from './Sections/WillApplySection'

import styles from './CleanupForm.css'

function reducer(state: CleanupFormState, action: CleanupFormAction) {
  switch (action.name) {
    case 'errors':
      return mergeIfDifferent(state, {
        errors: mergeIfDifferent(state.errors, action.value),
      })

    case 'branchPattern':
      return mergeIfDifferent(state, {
        branchPattern: branchPatternReducer(state.branchPattern, action),
      })

    case 'limit':
      return mergeIfDifferent(state, {
        limit: action.value,
      })

    case 'activeBranch':
      return mergeIfDifferent(state, {
        activeBranch: action.value,
      })

    case 'personal':
      return mergeIfDifferent(state, {
        personal: action.value,
      })

    case 'status':
      return mergeIfDifferent(state, {
        status: action.value,
      })

    case 'tags':
      return mergeIfDifferent(state, {
        tags: action.value,
      })

    case 'willApply':
      return mergeIfDifferent(state, {
        willApply: action.value,
      })

    case 'preserveArtifacts':
      return mergeIfDifferent(state, {
        preserveArtifacts: action.value,
      })

    case 'preserve':
      return mergeIfDifferent(state, {
        preserve: action.value,
      })

    default:
      return state
  }
}

function getInitialState(rule: Rule | null | undefined): CleanupFormState {
  return {
    errors: {
      preserve: null,
      limits: null,
    },
    limit: rule?.limit ?? defaultLimitValueData,
    activeBranch: branchesActivityConverter.toForm(rule?.filters.activeBranch),
    branchPattern: branchPatternConverter.toForm(rule?.filters.branchPattern),
    personal: personalConverter.toForm(rule?.filters.personalBuild),
    status: buildStatusConverter.toForm(rule?.filters.buildStatus),
    tags: tagsConverter.toForm(rule?.filters.tags?.tagsList),
    willApply: willApplyConverter.toForm(rule?.partitions),
    preserveArtifacts: rule?.preserveArtifacts ?? defaultPreserveArtifacts,
    preserve: preserveConverter.toForm(rule?.keepBuildParts),
  }
}

function CleanupForm({
  holderNode,
  onComplete = noop,
  onCancel = noop,
  loading,
  customError,
  ruleId,
  projectId,
  readOnlyProjectsKey,
}: Props): React.ReactElement {
  const arg = useContext(GetRulesArgContext)
  const {rule} = restApi.endpoints.getCleanupRules.useQuery(arg, {
    selectFromResult: ({data}) => ({
      rule: data?.[getKey(holderNode)]?.rules.find(item => item.ruleId === ruleId),
    }),
  })
  const [createRule] = restApi.endpoints.createCleanupRule.useMutation()
  const [updateRule] = restApi.endpoints.updateCleanupRule.useMutation()

  const saveRule = useCallback(
    (newRule: Rule) => {
      const ownership = getRuleOwnership(rule)

      if (rule?.ruleId && ownership.inherited) {
        return createRule({holderNode, rule: {...newRule}, ruleId: rule.ruleId})
      }

      if (rule?.ruleId && ownership.own) {
        return updateRule({holderNode, rule: {...newRule, ruleId: rule.ruleId}})
      } else {
        return createRule({holderNode, rule: {...newRule, source: 'own'}})
      }
    },
    [rule, createRule, updateRule, holderNode],
  )

  const readOnly = useIsReadOnly(projectId, readOnlyProjectsKey)
  const prevIsLoading = usePrevious(loading)
  const ownership = getRuleOwnership(rule)
  const [state, dispatch] = React.useReducer(reducer, rule, getInitialState)
  const setError = React.useCallback(
    (value: CleanupFormValidationErrors) =>
      dispatch({
        name: 'errors',
        value,
      }),
    [dispatch],
  )
  const hasCompleted = prevIsLoading && !loading && customError == null
  React.useEffect(() => {
    if (hasCompleted) {
      onComplete()
    }
  }, [hasCompleted, onComplete])

  const submitForm = (event: React.SyntheticEvent) => {
    event.preventDefault()
    const adaptedRule = adaptRuleUiToApi(state, rule?.ruleDisabled)
    saveRule(adaptedRule)
  }

  const cancel = React.useCallback(
    (event: React.MouseEvent<any>) => {
      event.preventDefault()
      onCancel()
    },
    [onCancel],
  )
  const disableSubmit = Object.values(state.errors).some(value => value != null)
  const holderName = holderNode.name
  const title = `Keep rule${typeof holderName === 'string' ? ` for\u00a0${holderName}` : ''}`
  const header = React.useMemo(() => <Header>{title}</Header>, [title])
  const content = React.useMemo(
    () => (
      <Content>
        <div className={styles.fields}>
          {ownership.inherited && (
            <p className={styles.ownershipDescription}>
              {'This rule has been inherited from a build configuration template.'}
            </p>
          )}
          {ownership.enforced && (
            <>
              <p className={styles.ownershipDescription}>
                {
                  "This rule has been defined and enforced by Default template. It's the Read-only Rule. To change this, please go to the Project settings and change certain Rules for Enforced template."
                }
              </p>
              <p>{'Caution: it will affect all descendants.'}</p>
            </>
          )}
          <PreserveSection holderNode={holderNode} value={state.preserve} setError={setError} />
          <PreserveDependencies value={state.preserveArtifacts} />
          <H3 className={styles.title}>{'Build Filter'}</H3>
          <span className={styles.hint}>{'Defines requirements to preserved builds'}</span>
          <div className={styles.rowGroup}>
            <BuildStatusSection value={state.status} />
            <TagSection value={state.tags} holderNode={holderNode} />
            <PersonalSection value={state.personal} />
            <BranchesSection
              activeBranch={state.activeBranch}
              branchPattern={state.branchPattern}
              holderNode={holderNode}
            />
          </div>
          <LimitsSection value={state.limit} setError={setError} />
          <WillApplySection value={state.willApply} />
        </div>
      </Content>
    ),
    [
      holderNode,
      ownership.enforced,
      ownership.inherited,
      setError,
      state.activeBranch,
      state.branchPattern,
      state.limit,
      state.personal,
      state.preserve,
      state.preserveArtifacts,
      state.status,
      state.tags,
      state.willApply,
    ],
  )
  return (
    <LoadingContext.Provider value={loading || ownership.enforced || readOnly}>
      <DispatchContext.Provider value={dispatch}>
        <Dialog
          label={title}
          showCloseButton
          contentClassName={styles.dialog}
          onEscPress={onCancel}
          onCloseClick={onCancel}
          trapFocus
          show
          autoFocusFirst={false}
        >
          {header}
          <div className={classNames(styles.hint, styles.headerHint)}>
            {'Controls what\u00a0data to preserve\u00a0during clean-up'}
            <HelpLink
              url={resolveHelpURL('clean-up.html#Clean-up-KeepRule')}
              Component={IconButton}
              icon="help"
            />
          </div>
          <Form className={styles.form} onSubmit={submitForm}>
            {content}
            <Panel className={styles.panel}>
              {ownership.enforced || readOnly ? (
                <Button onClick={cancel}>{'Return'}</Button>
              ) : (
                <>
                  <Button primary disabled={disableSubmit} loader={loading} type={'submit'}>
                    {ownership.inherited ? 'Override' : ownership.own && rule ? 'Update' : 'Create'}
                  </Button>
                  <Button onClick={cancel}>{'Cancel'}</Button>
                </>
              )}
            </Panel>
          </Form>
        </Dialog>
      </DispatchContext.Provider>
    </LoadingContext.Provider>
  )
}

export default CleanupForm
